import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.slug === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
              </div>
              <p className="text-foreground-500 mb-4">文章未找到</p>
              <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://huangyouzaxianwan.com/article/${article.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '文章攻略', path: '/article/top-10-butter-games-2025' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
                <span className="text-xs text-foreground-400">
                  <i className="ri-eye-line mr-0.5"></i>
                  {article.views.toLocaleString()}次阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
              </div>

              <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                [&>h3]:text-lg [&>h3]:font-heading [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-3
                [&>p]:mb-4 [&>p]:text-sm [&>p]:leading-relaxed
                [&>ul]:mb-4 [&>li]:text-sm"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}